import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AdditionalDiscount,
  CabinInfo,
  CabinPreferenceType,
  CabinTypes,
  Categories,
  CompareDiscountsResponse,
  Deck,
  Discount,
  DiscountType,
  InsuranceProducts,
  IPriceToBookResponse,
  Item,
  PriceType,
  UpgradeItem,
  VoyagersMemberInfo,
} from '../../../../../interfaces/src/lib/interfaces/index';
import {
  AgeRule,
  BookingResult,
  CabinCategoryDataProps,
  ConditionMultifieldItem,
  GuestData,
  InputState,
  InsuranceItem,
  PackageItemOrder,
  Payment,
  PaymentMethod,
  PaymentType,
  ToasterItem,
  ToasterNotification,
  GenericToaster,
} from '../../../../../interfaces/src/lib/types/index';
import { getQueryParam } from '../../genericUtils/analyticsUtils';
import { getAge } from '../../utilFuncs/utilFuncs';
import { getPromotionAges } from '../../bookingFunnelUtils/bookingFunnelUtils';
import { loadFromSessionStorage } from '../../genericUtils/genericUtils';

type SelectedPriceTypes = {
  selectedPriceType: string;
  selectedPriceCode: string;
};

type StepInfo = {
  id: number;
  name: string;
};

export type BookingStepsType = {
  currentStep: StepInfo;
  firstCabinSelections: CabinSelectionsType;
  secondCabinSelections: CabinSelectionsType;
  cabinNum: number;
  selectedPriceValues: SelectedPriceTypes;
  selectedPriceType: PriceType | null;
  selectedInsurance: InsuranceItem[];
  insuranceProducts: InsuranceProducts[] | null;
  insuranceLabels: InsuranceItem[] | null;
  emergencyContact: EmergencyContact;
  eCouponCode: ECouponType;
  ageLimitDiscounts: AdditionalDiscount[] | null;
  selectedDiningOption: string;
  paymentMethods: Payment[];
  merchantIdBooking: string;
  isShareModalOpen: boolean;
  isDepartureFlightModalOpen: boolean;
  isDownloadModalOpen: boolean;
  isErrorModalOpen: boolean;
  isInsuranceModalOpen: boolean;
  hasInsuranceModalBeenClosed: boolean;
  isInsuranceModalConfigured: boolean;
  showModalOnlyOnce?: boolean;
  unavailableCabinCategoryCodes: string[] | null;
  prices?: IPriceToBookResponse | null;
  bookingResult: BookingResult;
  disabledPassengersInput: InputState[];
  toasterComparisonMessage: string;
  labelItem: string;
  serviceLabels: ServiceLabels[];
  paymentToasters: ToasterNotification[];
  is3dsFlow: boolean;
  is3dsBookingSuccess: boolean;
  packageItemsOrder: PackageItemOrder[];
  isAgeRestrictionChecked?: boolean;
  isSaveAndContinueClicked?: boolean;
  isTrainIncluded?: boolean;
  hasInsuranceStepConfigured?: boolean;
  hasInsurance: boolean;
  europins010Toaster?: GenericToaster | null;
  isProcessingEuropins010Warning?: boolean;
};

export type CabinSelectionsType = {
  lastDiscountNotification?: { discounts?: Discount[]; paxType?: string };
  selectedMacroCategoryObj: CabinTypes | null;
  selectedCabinPreferenceObj: CabinPreferenceType | null;
  selectedCategory: Categories | null;
  selectedCategoryWithCFData: CabinCategoryDataProps | null;
  selectedDeck: Deck | null;
  selectedMaxOccupancy?: number;
  selectedMinOccupancy?: number;
  selectedCabinPreference: string;
  selectedCategoryCode: string;
  selectedMacroCategory: string;
  selectedMacroCategoryTitle: string;
  selectedCabinType: string;
  nextCabinUpgradeObj: UpgradeItem | undefined;
  cabinPreferenceSkippedStep?: string;
  cabinPreferenceSkippedInformation?: ConditionMultifieldItem[];
  isDeckViewSkipped: boolean;
  upgraded: boolean;
  cabinObj: CabinInfo;
  serviceChargesToasterProps: any;
  extraServices?: Item[];
  selectedExtraServices: Item[];
  areMandatoryServicesValid: boolean;
  passengersData: GuestData[] | [];
  voyagersMembersInfo: VoyagersMemberInfo[];
  availableDiscounts: DiscountType[];
  vcDiscounts?: AdditionalDiscount[] | null;
  discountsToBeApplied: CompareDiscountsResponse | null;
  vcPassengerType: string;
  discountToasters: ToasterItem[];
  isTrainIncluded?: boolean;
};

export type EmergencyContact = {
  firstName: string;
  prefix: string;
  phone: string;
};

export type ECouponType = {
  code: string;
  discountAmount: string;
  isMultiusage: boolean;
};

export type ServiceLabels = {
  code: string;
  type: string;
  description: string;
  longDescription: string;
};

const initialState: BookingStepsType = {
  currentStep: {
    id: 0,
    name: '',
  },
  firstCabinSelections: {
    selectedMacroCategoryObj: null,
    selectedCabinPreferenceObj: null,
    selectedCategory: null,
    selectedDeck: null,
    selectedMaxOccupancy: 15,
    selectedCabinPreference: '',
    selectedCategoryCode: '',
    selectedMacroCategory: '',
    selectedMacroCategoryTitle: '',
    selectedCabinType: '',
    nextCabinUpgradeObj: undefined,
    isDeckViewSkipped: false,
    upgraded: false,
    cabinObj: {
      priceBeforeDiscount: '',
      price: '',
      hotelServiceChargesCodes: [],
      hotelServiceChargesType: '',
      hotelServiceChargesAdult: '',
      hotelServiceChargesChild: '',
      hotelServiceChargesInfant: '',
      hotelServiceChargesSenior: '',
      hotelServiceChargesJunior: '',
      hotelServiceChargesTotal: '',
      insurance: [],
    },
    serviceChargesToasterProps: {
      showToaster: false,
      type: 'increase',
      enablePriceIncreaseToaster: false,
      enablePriceDecreaseToaster: false,
      priceIncreaseTitle: '',
      priceIncreaseMessage: '',
      priceIncreasePosition: '',
      priceIncreaseBackgroundColor: '',
      priceIncreaseIconColor: '',
      priceIncreaseShowIcon: false,
      priceIncreaseShowCloseButton: false,
      priceIncreaseAutoCloseTime: 0,
      priceDecreaseTitle: '',
      priceDecreaseMessage: '',
      priceDecreasePosition: '',
      priceDecreaseBackgroundColor: '',
      priceDecreaseIconColor: '',
      priceDecreaseShowIcon: false,
      priceDecreaseShowCloseButton: false,
      priceDecreaseAutoCloseTime: 0,
    },
    extraServices: [],
    selectedExtraServices: [],
    areMandatoryServicesValid: true,
    selectedCategoryWithCFData: null,
    passengersData: loadFromSessionStorage('firstCabinSelectionsData', []),
    voyagersMembersInfo: [],
    vcDiscounts: [],
    availableDiscounts: [],
    discountsToBeApplied: null,
    vcPassengerType: '',
    discountToasters: [],
  },
  secondCabinSelections: {
    selectedMacroCategoryObj: null,
    selectedCabinPreferenceObj: null,
    selectedCategory: null,
    selectedDeck: null,
    nextCabinUpgradeObj: undefined,
    upgraded: false,
    isDeckViewSkipped: false,
    selectedMaxOccupancy: 15,
    selectedCabinPreference: '',
    selectedCategoryCode: '',
    selectedMacroCategory: '',
    selectedMacroCategoryTitle: '',
    selectedCabinType: '',
    cabinObj: {
      priceBeforeDiscount: '',
      price: '',
      hotelServiceChargesCodes: [],
      hotelServiceChargesType: '',
      hotelServiceChargesAdult: '',
      hotelServiceChargesChild: '',
      hotelServiceChargesInfant: '',
      hotelServiceChargesSenior: '',
      hotelServiceChargesJunior: '',
      hotelServiceChargesTotal: '',
      insurance: [],
    },
    serviceChargesToasterProps: {
      showToaster: false,
      type: 'increase',
      enablePriceIncreaseToaster: false,
      enablePriceDecreaseToaster: false,
      priceIncreaseTitle: '',
      priceIncreaseMessage: '',
      priceIncreasePosition: '',
      priceIncreaseBackgroundColor: '',
      priceIncreaseIconColor: '',
      priceIncreaseShowIcon: false,
      priceIncreaseShowCloseButton: false,
      priceIncreaseAutoCloseTime: 0,
      priceDecreaseTitle: '',
      priceDecreaseMessage: '',
      priceDecreasePosition: '',
      priceDecreaseBackgroundColor: '',
      priceDecreaseIconColor: '',
      priceDecreaseShowIcon: false,
      priceDecreaseShowCloseButton: false,
      priceDecreaseAutoCloseTime: 0,
    },
    extraServices: [],
    selectedExtraServices: [],
    areMandatoryServicesValid: true,
    selectedCategoryWithCFData: null,
    passengersData: loadFromSessionStorage('secondCabinSelectionsData', []),
    voyagersMembersInfo: [],
    vcDiscounts: [],
    availableDiscounts: [],
    discountsToBeApplied: null,
    vcPassengerType: '',
    discountToasters: [],
  },
  cabinNum: 1,
  selectedPriceType: null,
  selectedPriceValues: {
    selectedPriceType: '',
    selectedPriceCode: '',
  },
  selectedInsurance: [],
  insuranceProducts: null,
  insuranceLabels: null,
  emergencyContact: {
    firstName: '',
    prefix: '',
    phone: '',
  },
  selectedDiningOption: '',
  eCouponCode: {
    code: '',
    discountAmount: '',
    isMultiusage: false,
  },
  ageLimitDiscounts: null,
  paymentMethods: [
    {
      type: PaymentType.TOTALTRAVEL,
      method: PaymentMethod.SECURE_FIELDS,
      active: true,
    },
    {
      type: PaymentType.DEPOSIT,
      method: PaymentMethod.SECURE_FIELDS,
      active: false,
    },
    {
      type: PaymentType.INSTALLMENTS,
      method: PaymentMethod.SECURE_FIELDS,
      active: false,
    },
  ],
  isTrainIncluded: false,
  merchantIdBooking: '',
  unavailableCabinCategoryCodes: null,
  isShareModalOpen: false,
  isDepartureFlightModalOpen: false,
  isErrorModalOpen: false,
  isInsuranceModalOpen: false,
  hasInsuranceModalBeenClosed: false,
  isInsuranceModalConfigured: false,
  showModalOnlyOnce: false,
  prices: null,
  isDownloadModalOpen: false,
  bookingResult: {
    warnings: [],
    bookingType: '',
    bookingNumber: -1,
    totalPaymentReceived: '',
  },
  disabledPassengersInput: [],
  toasterComparisonMessage: '',
  paymentToasters: [],
  labelItem: '',
  serviceLabels: [],
  is3dsFlow:
    getQueryParam('status_3d') === 'Y' ||
    !!(getQueryParam('status') && getQueryParam('datatransTrxId')),
  is3dsBookingSuccess: false,
  packageItemsOrder: [],
  isAgeRestrictionChecked: false,
  isSaveAndContinueClicked: false,
  hasInsuranceStepConfigured: false,
  hasInsurance: false,
  europins010Toaster: null,
  isProcessingEuropins010Warning: false,
};

const getPreservedInsurance = <T>(
  currentInsurance: T[] | undefined,
  fallbackInsurance: T[] | undefined
) => (currentInsurance && currentInsurance.length > 0 ? currentInsurance : fallbackInsurance);

const bookingSteps = createSlice({
  name: 'bookingSteps',
  initialState: loadFromSessionStorage('bookingState', initialState),
  reducers: {
    setIsShareModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isShareModalOpen = action.payload;
    },

    setIsDepartureFlightModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isDepartureFlightModalOpen = action.payload;
    },

    setUnavailableCabinCategoryCodes: (state, action: PayloadAction<string[]>) => {
      state.unavailableCabinCategoryCodes = action.payload;
    },
    setIsDownloadModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isDownloadModalOpen = action.payload;
    },
    setIsErrorModalOpen: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.isErrorModalOpen = action.payload;
    },
    setIsInsuranceModalOpen: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.isInsuranceModalOpen = action.payload;
    },
    setHasInsuranceModalBeenClosed: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.hasInsuranceModalBeenClosed = action.payload;
    },
    setIsInsuranceModalConfigured: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.isInsuranceModalConfigured = action.payload;
    },
    setShowModalOnlyOnce: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.showModalOnlyOnce = action.payload;
    },
    setEuropins010Toaster: (
      state: BookingStepsType,
      action: PayloadAction<GenericToaster | null>
    ) => {
      state.europins010Toaster = action.payload;
    },
    setIsProcessingEuropins010Warning: (
      state: BookingStepsType,
      action: PayloadAction<boolean>
    ) => {
      state.isProcessingEuropins010Warning = action.payload;
    },
    setInsuranceProducts: (
      state: BookingStepsType,
      action: PayloadAction<InsuranceProducts[] | null>
    ) => {
      state.insuranceProducts = action.payload;
    },
    setInsuranceLabels: (
      state: BookingStepsType,
      action: PayloadAction<InsuranceItem[] | null>
    ) => {
      state.insuranceLabels = action.payload;
    },
    //first cabin actions
    setSelectedMaxOccupancyFirstCabin: (state: BookingStepsType, action: PayloadAction<number>) => {
      state.firstCabinSelections.selectedMaxOccupancy = action.payload;
    },

    setSelectedMinOccupancyFirstCabin: (state: BookingStepsType, action: PayloadAction<number>) => {
      state.firstCabinSelections.selectedMinOccupancy = action.payload;
    },

    setUpgradedFirstCabin: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.firstCabinSelections.upgraded = action.payload;
      return state;
    },
    setSelectedCabinPreferenceFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.firstCabinSelections.selectedCabinPreference = action.payload;
    },
    setSelectedPriceType: (state: BookingStepsType, action: PayloadAction<PriceType | null>) => {
      state.selectedPriceType = action.payload;
      state.firstCabinSelections.lastDiscountNotification = {
        discounts: action.payload?.lowestPricedCategory?.discounts,
        paxType: action.payload?.lowestPricedCategory?.passengerType,
      };
    },
    setSelectedPriceValues: (
      state: BookingStepsType,
      action: PayloadAction<SelectedPriceTypes>
    ) => {
      state.selectedPriceValues = action.payload;
    },
    setLabelItem: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.labelItem = action.payload;
    },
    setServiceLabels: (state: BookingStepsType, action: PayloadAction<ServiceLabels[]>) => {
      state.serviceLabels = action.payload;
    },
    setSelectedMacroCategoryObjFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinTypes | null>
    ) => {
      state.firstCabinSelections.selectedMacroCategoryObj = action.payload;
      state.firstCabinSelections.lastDiscountNotification = {
        discounts: action.payload?.lowestPricedCategory?.discounts,
        paxType: action.payload?.lowestPricedCategory?.passengerType,
      };
    },
    setSelectedCabinPreferenceObjFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinPreferenceType>
    ) => {
      state.firstCabinSelections.selectedCabinPreferenceObj = action.payload;
      state.firstCabinSelections.lastDiscountNotification = {
        discounts: action.payload?.lowestPricedCategory?.discounts,
        paxType: action.payload?.lowestPricedCategory?.passengerType,
      };
    },
    setSelectedCategoryFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<Categories | null>
    ) => {
      state.firstCabinSelections.selectedCategory = action.payload;
      if (action.payload) {
        state.firstCabinSelections.lastDiscountNotification = {
          discounts: action.payload?.discounts,
          paxType: action.payload?.passengerType,
        };
      }
    },
    setSelectedCategoryCodeFirstCabin: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.firstCabinSelections.selectedCategoryCode = action.payload;
    },
    setSelectedMacroCategoryFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.firstCabinSelections.selectedMacroCategory = action.payload;
    },
    setSelectedMacroCategoryTitleFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.firstCabinSelections.selectedMacroCategoryTitle = action.payload;
    },
    setSelectedCabinTypeFirstCabin: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.firstCabinSelections.selectedCabinType = action.payload;
    },
    setCabinObjFirstCabin: (state: BookingStepsType, action: PayloadAction<CabinInfo>) => {
      state.firstCabinSelections.cabinObj = action.payload;
    },
    setServiceChargesToasterPropsFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<any>
    ) => {
      state.firstCabinSelections.serviceChargesToasterProps = action.payload;
    },
    setSelectedExtraServicesFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<Item[]>
    ) => {
      state.firstCabinSelections.selectedExtraServices = action.payload;
      return state;
    },
    setExtraServicesFirstCabin: (state: BookingStepsType, action: PayloadAction<Item[]>) => {
      state.firstCabinSelections.extraServices = action.payload;
      return state;
    },
    setSelectedCategoryWithCFDataFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinCategoryDataProps | null>
    ) => {
      state.firstCabinSelections.selectedCategoryWithCFData = action.payload;
      return state;
    },
    setFirstCabinMembership: (
      state: BookingStepsType,
      action: PayloadAction<VoyagersMemberInfo>
    ) => {
      state.firstCabinSelections.voyagersMembersInfo =
        state.firstCabinSelections.voyagersMembersInfo.some(
          (member) => member.guestIndex && member.guestIndex + 1 === action.payload.guestIndex
        )
          ? state.firstCabinSelections.voyagersMembersInfo
          : [...state.firstCabinSelections.voyagersMembersInfo, action.payload];

      const dob = action.payload.memberInfo?.dateOfBirth;
      const departureDate = action.payload.departureDate;

      if (state.firstCabinSelections.passengersData.length > 0 && departureDate && dob) {
        const currentAge = getAge(new Date(dob), new Date(departureDate));

        state.firstCabinSelections.passengersData = state.firstCabinSelections.passengersData.map(
          (passenger) =>
            passenger.number === (action.payload.guestIndex ?? -1) + 1
              ? { ...passenger, age: currentAge }
              : passenger
        );
      }
      return state;
    },
    removeFirstCabinMembership: (
      state: BookingStepsType,
      action: PayloadAction<{ cardNumber: string; ageRule: AgeRule; guestIndex: number }>
    ) => {
      const promotionAges = getPromotionAges(action.payload.ageRule);

      //Reset age of the guest who is removing the membership
      if (state.firstCabinSelections.passengersData.length > 0) {
        state.firstCabinSelections.passengersData = state.firstCabinSelections.passengersData.map(
          (passenger) =>
            passenger.number === action.payload.guestIndex + 1
              ? { ...passenger, age: promotionAges?.adultAge || 30 }
              : passenger
        );
      }

      state.firstCabinSelections.voyagersMembersInfo =
        state.firstCabinSelections.voyagersMembersInfo.filter(
          (member) => member?.memberInfo?.membership?.number !== action.payload.cardNumber
        );

      return state;
    },
    setNextCabinUpgradeObj: (
      state: BookingStepsType,
      action: PayloadAction<{ cabinNum: number; item: UpgradeItem | undefined }>
    ) => {
      if (action.payload.cabinNum === 1) {
        state.firstCabinSelections.nextCabinUpgradeObj = action.payload.item;
        return state;
      }

      state.secondCabinSelections.nextCabinUpgradeObj = action.payload.item;
      return state;
    },
    setCabinPreferenceSkippedStep: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.firstCabinSelections.cabinPreferenceSkippedStep = action.payload;
    },
    setCabinPreferenceSkippedInformation: (
      state: BookingStepsType,
      action: PayloadAction<ConditionMultifieldItem[] | undefined>
    ) => {
      state.firstCabinSelections.cabinPreferenceSkippedInformation = action.payload;
    },
    setDeckViewSkipped: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.firstCabinSelections.isDeckViewSkipped = action.payload;
    },
    //second cabin actions
    setUpgradedSecondCabin: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.secondCabinSelections.upgraded = action.payload;
      return state;
    },
    setSelectedMaxOccupancySecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<number>
    ) => {
      state.secondCabinSelections.selectedMaxOccupancy = action.payload;
    },

    setSelectedMinOccupancySecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<number>
    ) => {
      state.secondCabinSelections.selectedMinOccupancy = action.payload;
    },
    setSelectedCabinPreferenceSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.secondCabinSelections.selectedCabinPreference = action.payload;
    },
    setSelectedMacroCategoryObjSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinTypes | null>
    ) => {
      state.secondCabinSelections.selectedMacroCategoryObj = action.payload;
    },
    setSelectedCabinPreferenceObjSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinPreferenceType>
    ) => {
      state.secondCabinSelections.selectedCabinPreferenceObj = action.payload;
    },
    setSelectedCategorySecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<Categories | null>
    ) => {
      state.secondCabinSelections.selectedCategory = action.payload;
    },
    setSelectedCategoryCodeSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.secondCabinSelections.selectedCategoryCode = action.payload;
    },
    setSelectedMacroCategorySecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.secondCabinSelections.selectedMacroCategory = action.payload;
    },
    setSelectedMacroCategoryTitleSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<string>
    ) => {
      state.secondCabinSelections.selectedMacroCategoryTitle = action.payload;
    },
    setSelectedCabinTypeSecondCabin: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.secondCabinSelections.selectedCabinType = action.payload;
    },
    setCabinObjSecondCabin: (state: BookingStepsType, action: PayloadAction<CabinInfo>) => {
      state.secondCabinSelections.cabinObj = action.payload;
    },
    setSelectedExtraServicesSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<Item[]>
    ) => {
      state.secondCabinSelections.selectedExtraServices = action.payload;
      return state;
    },
    setExtraServicesSecondCabin: (state: BookingStepsType, action: PayloadAction<Item[]>) => {
      state.secondCabinSelections.extraServices = action.payload;
      return state;
    },
    setAreMandatoryServicesValid: (
      state: BookingStepsType,
      action: PayloadAction<{ cabinNum: number; isValid: boolean }>
    ) => {
      if (action.payload.cabinNum === 1) {
        state.firstCabinSelections.areMandatoryServicesValid = action.payload.isValid;
      } else {
        state.secondCabinSelections.areMandatoryServicesValid = action.payload.isValid;
      }
      return state;
    },
    setSelectedCategoryWithCFDataSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<CabinCategoryDataProps | null>
    ) => {
      state.secondCabinSelections.selectedCategoryWithCFData = action.payload;
      return state;
    },
    setSecondCabinMembership: (
      state: BookingStepsType,
      action: PayloadAction<VoyagersMemberInfo>
    ) => {
      state.secondCabinSelections.voyagersMembersInfo = [
        ...state.secondCabinSelections.voyagersMembersInfo,
        action.payload,
      ];
      return state;
    },
    removeSecondCabinMembership: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.secondCabinSelections.voyagersMembersInfo =
        state.secondCabinSelections.voyagersMembersInfo.filter(
          (member) => member?.memberInfo?.membership?.number !== action.payload
        );
      return state;
    },

    //common actions
    setCurrentStepName: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.currentStep.name = action.payload;
    },
    setCabinNum: (state: BookingStepsType, action: PayloadAction<number>) => {
      state.cabinNum = action.payload;
    },
    handleStepID: (state: BookingStepsType, action: PayloadAction<number>) => {
      state.currentStep.id = state.currentStep.id + action.payload;
    },
    setPassengersData: (state: BookingStepsType, action: PayloadAction<GuestData[]>) => {
      state.firstCabinSelections.passengersData = action.payload.filter(
        (passenger) => passenger.cabinIndex === 0
      );
      state.secondCabinSelections.passengersData = action.payload.filter(
        (passenger) => passenger.cabinIndex === 1
      );
    },
    setFirstPassengerPhoneNumber: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.firstCabinSelections.passengersData[0].phone = action.payload;
    },
    setFirstPassengerPrefixNumber: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.firstCabinSelections.passengersData[0].prefix = action.payload;
    },
    setECouponCode: (state: BookingStepsType, action: PayloadAction<ECouponType>) => {
      state.eCouponCode = action.payload;
    },
    setSelectedDiningOption: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.selectedDiningOption = action.payload;
    },
    setInsurance: (state: BookingStepsType, action: PayloadAction<InsuranceItem>) => {
      state.selectedInsurance = [...state.selectedInsurance, action.payload];
    },
    removeInsurance: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.selectedInsurance = state.selectedInsurance?.filter(
        (item) => item.code !== action.payload
      );
    },
    setEmergencyContact: (state: BookingStepsType, action: PayloadAction<EmergencyContact>) => {
      state.emergencyContact = action.payload;
    },
    setPaymentMethods: (state: BookingStepsType, action: PayloadAction<Payment[]>) => {
      state.paymentMethods = action.payload;
    },
    setBookingResult: (state: BookingStepsType, action: PayloadAction<BookingResult>) => {
      state.bookingResult = action.payload;
    },
    setPrices: (state: BookingStepsType, action: PayloadAction<IPriceToBookResponse | null>) => {
      state.prices = action.payload;
    },
    setDisabledPassengersInput: (state: BookingStepsType, action: PayloadAction<InputState[]>) => {
      state.disabledPassengersInput = action.payload;
    },
    setVCDiscountsFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<AdditionalDiscount[] | null>
    ) => {
      state.firstCabinSelections.vcDiscounts = action.payload;
      return state;
    },
    setVCDiscountsSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<AdditionalDiscount[] | null>
    ) => {
      state.secondCabinSelections.vcDiscounts = action.payload;
      return state;
    },
    addAgeLimitDiscounts: (
      state: BookingStepsType,
      action: PayloadAction<AdditionalDiscount[] | []>
    ) => {
      state.ageLimitDiscounts = action.payload;
    },
    setDiscountsToBeAppliedFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<CompareDiscountsResponse | null>
    ) => {
      state.firstCabinSelections.discountsToBeApplied = action.payload;
    },
    setDiscountToastersFirstCabin: (
      state: BookingStepsType,
      action: PayloadAction<ToasterItem[]>
    ) => {
      state.firstCabinSelections.discountToasters = action.payload;
    },
    setDiscountToastersSecondCabin: (
      state: BookingStepsType,
      action: PayloadAction<ToasterItem[]>
    ) => {
      state.secondCabinSelections.discountToasters = action.payload;
    },
    setToasterComparisonMessage: (state: BookingStepsType, action: PayloadAction<string>) => {
      state.toasterComparisonMessage = action.payload;
    },
    setPaymentErrorToasters: (
      state: BookingStepsType,
      action: PayloadAction<ToasterNotification[]>
    ) => {
      state.paymentToasters = action.payload;
    },
    setIs3dsFlow: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.is3dsFlow = action.payload;
    },
    setIs3dsBookingSuccess: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.is3dsBookingSuccess = action.payload;
    },
    setPackageItemsOrder: (state: BookingStepsType, action: PayloadAction<PackageItemOrder[]>) => {
      state.packageItemsOrder = action.payload;
    },
    setIsAgeRestrictionChecked: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.isAgeRestrictionChecked = action.payload;
    },
    setIsSaveAndContinueClicked: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.isSaveAndContinueClicked = action.payload;
    },
    setHasInsuranceStep: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.hasInsuranceStepConfigured = action.payload;
    },
    setHasInsurance: (state: BookingStepsType, action: PayloadAction<boolean>) => {
      state.hasInsurance = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase('resetFromPriceType', (state) => {
        state.selectedPriceType = initialState.selectedPriceType;
        state.selectedPriceValues = initialState.selectedPriceValues;
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        state.firstCabinSelections = {
          ...initialState.firstCabinSelections,
          lastDiscountNotification: state.firstCabinSelections.lastDiscountNotification,
          passengersData: state.firstCabinSelections.passengersData,
          voyagersMembersInfo: state.firstCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.firstCabinSelections.vcDiscounts,
        };
        state.secondCabinSelections = {
          ...initialState.secondCabinSelections,
          passengersData: state.secondCabinSelections.passengersData,
          voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.secondCabinSelections.vcDiscounts,
        };
        return state;
      })
      .addCase('resetFromOccupancy', (state) => {
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        state.firstCabinSelections = {
          ...initialState.firstCabinSelections,
          lastDiscountNotification: state.firstCabinSelections.lastDiscountNotification,
          selectedMaxOccupancy: state.firstCabinSelections.selectedMaxOccupancy,
          selectedMinOccupancy: state.firstCabinSelections.selectedMinOccupancy,
          cabinObj: {
            priceBeforeDiscount:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.beforeDiscount || '',
            price:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.afterDiscount || '',
            hotelServiceChargesCode:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.code ?? '',
            hotelServiceChargesType:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.type ?? '',
            hotelServiceChargesAdult:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perAdult ?? '',
            hotelServiceChargesChild:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perChild ?? '',
            hotelServiceChargesInfant:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perInfant ?? '',
            hotelServiceChargesSenior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perSenior ?? '',
            hotelServiceChargesJunior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perJunior ?? '',
            hotelServiceChargesTotal:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount?.total ??
              '',
            portChargesAmount: state?.selectedPriceType?.lowestPricedCategory?.portChargesAmount,
            insurance: getPreservedInsurance(
              state?.firstCabinSelections?.cabinObj?.insurance,
              state?.selectedPriceType?.lowestPricedCategory?.insurance
            ),
          },
          passengersData: state.firstCabinSelections.passengersData,
          voyagersMembersInfo: state.firstCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.firstCabinSelections.vcDiscounts,
        };
        state.secondCabinSelections = {
          ...initialState.secondCabinSelections,
          selectedMaxOccupancy: state.secondCabinSelections.selectedMaxOccupancy,
          selectedMinOccupancy: state.secondCabinSelections.selectedMinOccupancy,
          cabinObj: {
            priceBeforeDiscount:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.beforeDiscount || '',
            price:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.afterDiscount || '',
            hotelServiceChargesCode:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.code ?? '',
            hotelServiceChargesType:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.type ?? '',
            hotelServiceChargesAdult:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perAdult ?? '',
            hotelServiceChargesChild:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perChild ?? '',
            hotelServiceChargesInfant:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perInfant ?? '',
            hotelServiceChargesSenior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perSenior ?? '',
            hotelServiceChargesJunior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perJunior ?? '',
            hotelServiceChargesTotal:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount?.total ??
              '',
            portChargesAmount: state?.selectedPriceType?.lowestPricedCategory?.portChargesAmount,
            insurance: getPreservedInsurance(
              state?.secondCabinSelections?.cabinObj?.insurance,
              state?.selectedPriceType?.lowestPricedCategory?.insurance
            ),
          },
          passengersData: state.secondCabinSelections.passengersData,
          voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.secondCabinSelections.vcDiscounts,
        };
        return state;
      })
      .addCase('resetFromCabinType', (state, action: any) => {
        const cabinNum = action?.payload?.cabinNumber;
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        state.isTrainIncluded = initialState.isTrainIncluded;
        if (cabinNum === 1) {
          state.firstCabinSelections = {
            ...initialState.firstCabinSelections,
            selectedMacroCategoryObj: state.firstCabinSelections.selectedMacroCategoryObj,
            selectedMaxOccupancy: state.firstCabinSelections.selectedMaxOccupancy,
            selectedMinOccupancy: state.firstCabinSelections.selectedMinOccupancy,
            selectedCategoryCode: state.firstCabinSelections.selectedCategoryCode,
            selectedMacroCategory: state.firstCabinSelections.selectedMacroCategory,
            selectedMacroCategoryTitle: state.firstCabinSelections.selectedMacroCategoryTitle,
            selectedCabinType: state.firstCabinSelections.selectedCabinType,
            cabinObj: {
              priceBeforeDiscount:
                state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                  ?.beforeDiscount || '',
              price:
                state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                  ?.afterDiscount || '',
              hotelServiceChargesCode:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.code ?? '',
              hotelServiceChargesType:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.type ?? '',
              hotelServiceChargesAdult:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perAdult ?? '',
              hotelServiceChargesChild:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perChild ?? '',
              hotelServiceChargesInfant:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perInfant ?? '',
              hotelServiceChargesSenior:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perSenior ?? '',
              hotelServiceChargesJunior:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perJunior ?? '',
              hotelServiceChargesTotal:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.total ?? '',
              portChargesAmount: state?.selectedPriceType?.lowestPricedCategory?.portChargesAmount,
              insurance: getPreservedInsurance(
                state?.firstCabinSelections?.cabinObj?.insurance,
                state?.selectedPriceType?.lowestPricedCategory?.insurance
              ),
            },
            passengersData: state.firstCabinSelections.passengersData,
            voyagersMembersInfo: state.firstCabinSelections.voyagersMembersInfo,
            vcDiscounts: state.firstCabinSelections.vcDiscounts,
          };
        }

        state.secondCabinSelections = {
          ...initialState.secondCabinSelections,
          cabinObj: {
            priceBeforeDiscount:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.beforeDiscount || '',
            price:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.afterDiscount || '',
            hotelServiceChargesCode:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.code ?? '',
            hotelServiceChargesType:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.type ?? '',
            hotelServiceChargesAdult:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perAdult ?? '',
            hotelServiceChargesChild:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perChild ?? '',
            hotelServiceChargesInfant:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perInfant ?? '',
            hotelServiceChargesSenior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perSenior ?? '',
            hotelServiceChargesJunior:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                ?.perJunior ?? '',
            hotelServiceChargesTotal:
              state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount?.total ??
              '',
            portChargesAmount: state?.selectedPriceType?.lowestPricedCategory?.portChargesAmount,
            insurance: getPreservedInsurance(
              state?.secondCabinSelections?.cabinObj?.insurance,
              state?.selectedPriceType?.lowestPricedCategory?.insurance
            ),
          },
          passengersData: state.secondCabinSelections.passengersData,
          voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.secondCabinSelections.vcDiscounts,
        };
        return state;
      })
      .addCase('resetFromDeck', (state, action: any) => {
        const cabinNumber = action?.payload?.cabinNumber;
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        if (cabinNumber === 1) {
          state.firstCabinSelections.selectedExtraServices =
            initialState.firstCabinSelections.selectedExtraServices;
          state.secondCabinSelections = {
            ...initialState.secondCabinSelections,
            cabinObj: {
              priceBeforeDiscount:
                state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                  ?.beforeDiscount || '',
              price:
                state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                  ?.afterDiscount || '',
              hotelServiceChargesCode:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.code ?? '',
              hotelServiceChargesType:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.type ?? '',
              hotelServiceChargesAdult:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perAdult ?? '',
              hotelServiceChargesChild:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perChild ?? '',
              hotelServiceChargesInfant:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perInfant ?? '',
              hotelServiceChargesSenior:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perSenior ?? '',
              hotelServiceChargesJunior:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.perJunior ?? '',
              hotelServiceChargesTotal:
                state?.selectedPriceType?.lowestPricedCategory?.serviceCharges?.[0]?.amount
                  ?.total ?? '',
              portChargesAmount: state?.selectedPriceType?.lowestPricedCategory?.portChargesAmount,
              insurance: getPreservedInsurance(
                state?.secondCabinSelections?.cabinObj?.insurance,
                state?.selectedPriceType?.lowestPricedCategory?.insurance
              ),
            },
            passengersData: state.secondCabinSelections.passengersData,
            voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
            vcDiscounts: state.firstCabinSelections.vcDiscounts,
          };
        }
        state.secondCabinSelections = {
          ...state.secondCabinSelections,
          selectedExtraServices: initialState.secondCabinSelections.selectedExtraServices,
          passengersData: state.secondCabinSelections.passengersData,
          voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.secondCabinSelections.vcDiscounts,
        };
        return state;
      })
      .addCase('resetFromSummary', (state) => {
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        state.paymentMethods = initialState.paymentMethods;
        state.firstCabinSelections.discountToasters =
          initialState.firstCabinSelections.discountToasters;
        state.firstCabinSelections.discountToasters =
          initialState.secondCabinSelections.discountToasters;
      })
      .addCase('resetFromPassengerDetails', (state) => {
        state.insuranceLabels = initialState.insuranceLabels;
        state.insuranceProducts = initialState.insuranceProducts;
        state.selectedInsurance = initialState.selectedInsurance;
        state.prices = initialState.prices;
        state.paymentMethods = initialState.paymentMethods;
        state.firstCabinSelections.discountToasters =
          initialState.firstCabinSelections.discountToasters;
        state.firstCabinSelections.discountToasters =
          initialState.secondCabinSelections.discountToasters;
      })
      .addCase('resetFromCabinCategories', (state, action: any) => {
        const cabinNum = action?.payload?.cabinNumber;
        state.selectedInsurance = initialState.selectedInsurance;

        if (cabinNum === 1) {
          state.firstCabinSelections = {
            ...initialState.firstCabinSelections,
            selectedMacroCategoryObj: state.firstCabinSelections.selectedMacroCategoryObj,
            selectedMaxOccupancy: state.firstCabinSelections.selectedMaxOccupancy,
            selectedMinOccupancy: state.firstCabinSelections.selectedMinOccupancy,
            selectedCategoryCode: initialState.firstCabinSelections.selectedCategoryCode,
            setSelectedCategoryFirstCabin: initialState.firstCabinSelections.selectedCategory,
            selectedMacroCategory: state.firstCabinSelections.selectedMacroCategory,
            selectedMacroCategoryTitle: state.firstCabinSelections.selectedMacroCategoryTitle,
            selectedCabinType: state.firstCabinSelections.selectedCabinType,
            selectedCabinPreferenceObj: state.firstCabinSelections.selectedCabinPreferenceObj,
            selectedCabinPreference: state.firstCabinSelections.selectedCabinPreference,
            cabinObj: {
              priceBeforeDiscount:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.totalAllInclusiveCabinPrice?.priceBeforeDiscount || '',
              price:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.totalAllInclusiveCabinPrice?.afterDiscount || '',
              hotelServiceChargesCode:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.code ?? '',
              hotelServiceChargesType:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.type ?? '',
              hotelServiceChargesAdult:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.amount?.perAdult ?? '',
              hotelServiceChargesChild:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  .serviceCharges?.[0]?.amount?.perChild ?? '',
              hotelServiceChargesInfant:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.amount?.perInfant ?? '',
              hotelServiceChargesSenior:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.amount?.perSenior ?? '',
              hotelServiceChargesJunior:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.amount?.perJunior ?? '',
              hotelServiceChargesTotal:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.serviceCharges?.[0]?.amount?.total ?? '',
              portChargesAmount:
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.portChargesAmount,
              insurance: getPreservedInsurance(
                state?.firstCabinSelections?.cabinObj?.insurance,
                state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                  ?.insurance
              ),
            },
            passengersData: state.firstCabinSelections.passengersData,
            voyagersMembersInfo: state.firstCabinSelections.voyagersMembersInfo,
            vcDiscounts: state.firstCabinSelections.vcDiscounts,
          };
        }

        state.secondCabinSelections = {
          ...initialState.secondCabinSelections,
          cabinObj: {
            priceBeforeDiscount:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.priceBeforeDiscount || '',
            price:
              state?.selectedPriceType?.lowestPricedCategory?.totalAllInclusiveCabinPrice
                ?.afterDiscount || '',
            hotelServiceChargesCode:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.code ?? '',
            hotelServiceChargesType:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.type ?? '',
            hotelServiceChargesAdult:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.perAdult ?? '',
            hotelServiceChargesChild:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.perChild ?? '',
            hotelServiceChargesInfant:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.perInfant ?? '',
            hotelServiceChargesSenior:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.perSenior ?? '',
            hotelServiceChargesJunior:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.perJunior ?? '',
            hotelServiceChargesTotal:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.serviceCharges?.[0]?.amount?.total ?? '',
            portChargesAmount:
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory
                ?.portChargesAmount,
            insurance: getPreservedInsurance(
              state?.secondCabinSelections?.cabinObj?.insurance,
              state?.firstCabinSelections?.selectedMacroCategoryObj?.lowestPricedCategory?.insurance
            ),
          },
          passengersData: state.secondCabinSelections.passengersData,
          voyagersMembersInfo: state.secondCabinSelections.voyagersMembersInfo,
          vcDiscounts: state.secondCabinSelections.vcDiscounts,
        };
        return state;
      }),
});

export const {
  setIsShareModalOpen,
  setIsDepartureFlightModalOpen,
  setIsDownloadModalOpen,
  setIsErrorModalOpen,
  setSelectedMaxOccupancyFirstCabin,
  setSelectedMinOccupancyFirstCabin,
  setSelectedPriceType,
  setSelectedPriceValues,
  setSelectedMacroCategoryObjFirstCabin,
  setSelectedCabinPreferenceObjFirstCabin,
  setSelectedCategoryFirstCabin,
  setSelectedCabinPreferenceFirstCabin,
  setSelectedCategoryCodeFirstCabin,
  setSelectedMacroCategoryFirstCabin,
  setSelectedMacroCategoryTitleFirstCabin,
  setSelectedCabinTypeFirstCabin,
  setCabinObjFirstCabin,
  setSelectedCategoryWithCFDataFirstCabin,
  setSelectedExtraServicesFirstCabin,
  setExtraServicesFirstCabin,
  setCabinPreferenceSkippedStep,
  setCabinPreferenceSkippedInformation,
  setDeckViewSkipped,
  setSelectedMaxOccupancySecondCabin,
  setSelectedMinOccupancySecondCabin,
  setSelectedMacroCategoryObjSecondCabin,
  setSelectedCabinPreferenceObjSecondCabin,
  setSelectedCategorySecondCabin,
  setSelectedCabinPreferenceSecondCabin,
  setSelectedCategoryCodeSecondCabin,
  setSelectedMacroCategorySecondCabin,
  setSelectedMacroCategoryTitleSecondCabin,
  setSelectedCabinTypeSecondCabin,
  setCabinObjSecondCabin,
  setSelectedExtraServicesSecondCabin,
  setExtraServicesSecondCabin,
  setAreMandatoryServicesValid,
  setSelectedCategoryWithCFDataSecondCabin,
  setCurrentStepName,
  handleStepID,
  setCabinNum,
  setPassengersData,
  setFirstPassengerPhoneNumber,
  setFirstPassengerPrefixNumber,
  setECouponCode,
  setSelectedDiningOption,
  setInsurance,
  removeInsurance,
  setEmergencyContact,
  setPaymentMethods,
  setUnavailableCabinCategoryCodes,
  setBookingResult,
  setPrices,
  setFirstCabinMembership,
  setSecondCabinMembership,
  removeFirstCabinMembership,
  removeSecondCabinMembership,
  setDisabledPassengersInput,
  setNextCabinUpgradeObj,
  setUpgradedSecondCabin,
  setUpgradedFirstCabin,
  setVCDiscountsFirstCabin,
  setVCDiscountsSecondCabin,
  setDiscountsToBeAppliedFirstCabin,
  setLabelItem,
  setServiceLabels,
  addAgeLimitDiscounts,
  setDiscountToastersFirstCabin,
  setDiscountToastersSecondCabin,
  setToasterComparisonMessage,
  setPaymentErrorToasters,
  setIs3dsFlow,
  setIs3dsBookingSuccess,
  setPackageItemsOrder,
  setIsAgeRestrictionChecked,
  setIsSaveAndContinueClicked,
  setHasInsuranceStep,
  setHasInsurance,
  setServiceChargesToasterPropsFirstCabin,
  setInsuranceProducts,
  setInsuranceLabels,
  setIsInsuranceModalOpen,
  setHasInsuranceModalBeenClosed,
  setIsInsuranceModalConfigured,
  setShowModalOnlyOnce,
  setEuropins010Toaster,
  setIsProcessingEuropins010Warning,
} = bookingSteps.actions;
export default bookingSteps.reducer;
